import { openDB } from 'idb'
import { encryptIdbRecord, decryptIdbRecord } from 'src/emr/database/idbCrypto'

/**
 * Lightweight, self-contained IndexedDB cache for short appointment diagnoses.
 *
 * Deliberately a SEPARATE database from `erm-database` so we never bump that DB's version (its
 * upgrade wipes every EMR store and forces a full re-sync). This cache only ever holds tiny,
 * encrypted "short diagnosis" strings keyed by booked-event id, and prunes itself daily.
 *
 * Records: { eventId (plaintext key), dayKey (event date, used for pruning), + encrypted label/kind }.
 *  - label: short PHI text ('' means "checked, none")
 *  - kind:  'dx' (diagnosis) | 'cc' (complaint) | 'pc' (presenting concern) | 'tx' (therapy)
 * The label is PHI, so the record is encrypted at rest with the shared EMR crypto key
 * (falls back to plaintext only on insecure contexts where encryption is unavailable).
 */

const DB_NAME = 'appt-diagnosis-cache'
const DB_VERSION = 1
const STORE = 'event_diagnosis'
const PRUNE_GUARD_KEY = 'apptDiagnosisCache:lastPrunedOn'
const KEEP_DAYS = 7

let dbPromise = null

function getDB() {
  if (typeof indexedDB === 'undefined') {
    return Promise.resolve(null)
  }
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'eventId' })
          store.createIndex('dayKeyIDX', 'dayKey')
        }
      },
    }).catch((err) => {
      console.warn('appt-diagnosis-cache open failed', err)
      return null
    })
  }
  return dbPromise
}

export async function getDiagnosisFromCache(eventId) {
  if (!eventId) return null
  try {
    const db = await getDB()
    if (!db) return null
    const stored = await db.get(STORE, eventId)
    if (!stored) return null
    const record = await decryptIdbRecord(stored)
    if (!record) return null
    // New shape: { eventId, dayKey, label, kind }. Back-compat: old records only had `diagnosis`.
    if (typeof record.label === 'string') {
      return { ...record, kind: record.kind || 'dx' }
    }
    if (typeof record.diagnosis === 'string') {
      return { eventId: record.eventId, dayKey: record.dayKey, label: record.diagnosis, kind: 'dx' }
    }
    return null
  } catch (err) {
    return null
  }
}

export async function putDiagnosisInCache({ eventId, label, kind, dayKey }) {
  if (!eventId) return
  try {
    const db = await getDB()
    if (!db) return
    const record = { eventId, dayKey: dayKey || '', label: label || '', kind: kind || 'dx' }
    // Encrypt everything except the keyPath; keep dayKey readable for pruning.
    const encrypted = await encryptIdbRecord(record, 'eventId')
    if (encrypted && encrypted.__enc) {
      encrypted.dayKey = record.dayKey
    }
    await db.put(STORE, encrypted)
  } catch (err) {
    /* cache write is best-effort */
  }
}

/**
 * Drop cached entries older than KEEP_DAYS — runs at most once per calendar day per tab.
 * Uses the plaintext dayKey index so no decryption is needed.
 */
export async function pruneOldDiagnosisCacheOncePerDay() {
  try {
    const todayKey = new Date().toISOString().slice(0, 10)
    if (typeof localStorage !== 'undefined' && localStorage.getItem(PRUNE_GUARD_KEY) === todayKey) {
      return
    }
    const db = await getDB()
    if (!db) return

    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - KEEP_DAYS)
    const cutoffKey = cutoff.toISOString().slice(0, 10)

    const tx = db.transaction(STORE, 'readwrite')
    const index = tx.store.index('dayKeyIDX')
    // Delete everything with dayKey strictly below the cutoff (lexical compare works for YYYY-MM-DD).
    let cursor = await index.openCursor(window.IDBKeyRange.upperBound(cutoffKey, true))
    while (cursor) {
      await cursor.delete()
      cursor = await cursor.continue()
    }
    await tx.done

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(PRUNE_GUARD_KEY, todayKey)
    }
  } catch (err) {
    /* pruning is best-effort */
  }
}
